import { Link, createFileRoute, useRouter } from '@tanstack/react-router'
import type { OpsOrderDetail, OpsOrderRow } from '@hedgehog/shared'
import { opsApi } from '@/components/ops/api'
import { formatUsd } from '@/components/ops/format'
import { IssueList } from '@/components/ops/order-sections'
import { ErrorState, LoadingState } from '@/components/app/states'
import { formatAge } from '@/components/app/format'
import { useInterval } from '@/hooks/use-interval'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { cn } from '@/lib/utils'

// The list row has no last error or review issues, so the queue loads each order's detail.
// Ceiling: one request per queued order, capped at DETAIL_CAP. Upgrade path: include
// lastError and latest review issues in GET /ops/orders?state=needs_human.
const DETAIL_CAP = 40

interface QueueItem {
  row: OpsOrderRow
  detail: OpsOrderDetail | null
}

export const Route = createFileRoute('/ops/queue')({
  loader: async (): Promise<QueueItem[]> => {
    const { orders } = await opsApi<{ orders: OpsOrderRow[] }>('/ops/orders?state=needs_human')
    const sorted = [...orders].sort((a, b) => Number(b.overdue) - Number(a.overdue) || a.stateEnteredAt - b.stateEnteredAt)
    const details = await Promise.all(
      sorted.slice(0, DETAIL_CAP).map((o) => opsApi<OpsOrderDetail>(`/ops/orders/${encodeURIComponent(o.id)}`).catch(() => null)),
    )
    return sorted.map((row, i) => ({ row, detail: details[i] ?? null }))
  },
  head: () => ({ meta: [{ title: 'Needs human · Ops' }] }),
  component: QueuePage,
  pendingComponent: () => <LoadingState label="Loading queue" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not load the queue" />,
})

function QueuePage() {
  const items = Route.useLoaderData()
  const router = useRouter()
  useInterval(() => router.invalidate({ filter: (m) => m.routeId === Route.id }), 60_000)

  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-base font-semibold">
        Needs human <span className="font-normal text-muted-foreground">({items.length})</span>
      </h1>
      {items.length === 0 ? (
        <Empty className="border border-border">
          <EmptyHeader>
            <EmptyTitle>Queue is empty</EmptyTitle>
            <EmptyDescription>Third review fails, budget stops and pipeline errors land here.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map(({ row, detail }) => {
            const latestReview = detail ? [...detail.reviews].sort((a, b) => b.createdAt - a.createdAt)[0] : undefined
            const failedJob = detail?.jobs.find((j) => j.error)
            const lastError = detail?.order.lastError ?? failedJob?.error ?? null
            return (
              <li key={row.id} className={cn('rounded-lg border border-border bg-card p-3', row.overdue && 'border-destructive/50')}>
                <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <Link
                    to="/ops/orders/$orderId"
                    params={{ orderId: row.id }}
                    className="font-medium underline-offset-4 hover:underline focus-visible:underline"
                  >
                    {row.businessName}
                  </Link>
                  <span className="text-muted-foreground">{row.domain}</span>
                  <span className={cn('tabular-nums', row.overdue ? 'font-medium text-destructive' : 'text-muted-foreground')}>
                    waiting {formatAge(row.stateEnteredAt)}
                    {row.overdue && ' · overdue'}
                  </span>
                  <span className={cn('tabular-nums', row.costUsd > row.budgetUsd ? 'font-medium text-destructive' : 'text-muted-foreground')}>
                    {formatUsd(row.costUsd)}
                  </span>
                  {detail && <span className="text-muted-foreground">{detail.order.reviewFails} review fails</span>}
                </div>
                {!detail ? (
                  <p className="text-xs text-muted-foreground">Details not loaded. Open the order for the error and review issues.</p>
                ) : (
                  <div className="grid gap-3 lg:grid-cols-2">
                    <div>
                      <h2 className="mb-1 text-xs font-semibold">Last error</h2>
                      {lastError ? (
                        <pre className="max-h-40 overflow-auto rounded bg-muted p-2 font-mono text-xs whitespace-pre-wrap text-destructive">{lastError}</pre>
                      ) : (
                        <p className="text-xs text-muted-foreground">No error recorded.</p>
                      )}
                    </div>
                    <div>
                      <h2 className="mb-1 text-xs font-semibold">
                        Latest review issues
                        {latestReview && <span className="font-normal text-muted-foreground"> · {latestReview.pass ? 'pass' : 'fail'}, {latestReview.model ?? 'unknown model'}</span>}
                      </h2>
                      {latestReview ? <IssueList issues={latestReview.issues} /> : <p className="text-xs text-muted-foreground">No review yet.</p>}
                    </div>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
